import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShieldCheck, MapPin, CheckCircle2, Clock, FileText, Package, Home as HomeIcon, Building2, ArchiveRestore, Sparkles, Upload, X, Loader2, MessageCircle } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { api, BACKEND_URL } from "../lib/api";
import { track } from "../lib/pixel";
import { buildClearanceWaMessage, whatsappLink, WHATSAPP_NUMBER as FALLBACK_WA } from "../lib/whatsapp";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useCms } from "../lib/cms";

const REGION_COLS = [
  { title: "Aachen & Umgebung", places: ["Aachen", "Würselen", "Herzogenrath", "Alsdorf", "Stolberg", "Eschweiler", "Baesweiler", "Düren", "Kornelimünster", "Brand", "Burtscheid"] },
  { title: "Köln & Umgebung", places: ["Köln", "Ehrenfeld", "Nippes", "Lindenthal", "Sülz", "Deutz", "Kalk", "Porz", "Mülheim", "Rodenkirchen", "Leverkusen", "Bergisch Gladbach", "Frechen", "Hürth", "Pulheim", "Brühl"] },
  { title: "Düsseldorf & Umgebung", places: ["Düsseldorf", "Bilk", "Oberkassel", "Pempelfort", "Flingern", "Derendorf", "Gerresheim", "Benrath", "Neuss", "Ratingen", "Hilden", "Meerbusch", "Erkrath"] },
];

const WHAT_WE_CLEAR = [
  { icon: HomeIcon, title: "Wohnungsauflösung", text: "Diskrete, komplette Räumung Ihrer Wohnung." },
  { icon: Building2, title: "Haushaltsauflösung", text: "Vom kleinen Haus bis zur großen Immobilie." },
  { icon: ArchiveRestore, title: "Nachlassauflösung", text: "Respektvoll, ohne Aufsehen, mit Zeit für Erinnerungsstücke." },
  { icon: Package, title: "Keller & Dachboden", text: "Wir schaffen wieder Platz – schnell und sortiert." },
  { icon: Building2, title: "Garage & Gewerbeobjekte", text: "Vom Werkstattraum bis zum Ladenlokal." },
  { icon: Sparkles, title: "Messie-Wohnung (diskret)", text: "Sensibel, ohne Vorwürfe – wir helfen ruhig weiter." },
];

const PROCESS = [
  { icon: CheckCircle2, title: "Kostenlose Besichtigung", text: "Wir kommen vorbei, ohne Verpflichtung." },
  { icon: FileText, title: "Festpreis-Angebot", text: "Klar, verbindlich und schriftlich." },
  { icon: Trash2, title: "Räumung & fachgerechte Entsorgung", text: "Sortenrein, umweltgerecht und mit Nachweis." },
  { icon: Sparkles, title: "Besenreine Übergabe", text: "Übergabefertig für Vermieter, Käufer oder Erben." },
];

const REASSURANCE = [
  { icon: ShieldCheck, title: "Diskret und ohne Aufsehen", text: "Ruhige Abwicklung, ohne Aufsehen im Haus." },
  { icon: FileText, title: "Wertanrechnung möglich", text: "Verwertbare Gegenstände rechnen wir an und senken so Ihren Preis." },
  { icon: CheckCircle2, title: "Entsorgungsnachweis", text: "Sie erhalten einen Nachweis über die fachgerechte Entsorgung." },
];

const PRICE_TIERS = [
  { title: "Keller / Dachboden", price: "ab 390 €" },
  { title: "Wohnung 1–2 Zimmer", price: "ab 790 €" },
  { title: "Komplette Haushaltsauflösung", price: "ab 1.490 €" },
];

const FAQS = [
  { q: "Wie schnell ist ein Termin möglich?", a: "In der Region Aachen, Köln und Düsseldorf können wir oft innerhalb von 7 Tagen einen Termin anbieten – bei kurzfristigem Bedarf melden Sie sich einfach direkt per WhatsApp." },
  { q: "Was kostet eine Entrümpelung?", a: "Der Preis hängt von Menge, Etage, Zugang und Wertanrechnung ab. Nach der kostenlosen Besichtigung erhalten Sie einen verbindlichen Festpreis – ohne versteckte Kosten." },
  { q: "Ist die Besichtigung wirklich kostenlos?", a: "Ja. Die Vor-Ort-Besichtigung ist immer kostenlos und unverbindlich." },
  { q: "Bekomme ich einen Entsorgungsnachweis?", a: "Ja, auf Wunsch erhalten Sie einen schriftlichen Nachweis über die fachgerechte Entsorgung." },
  { q: "Werden verwertbare Gegenstände angerechnet?", a: "Ja. Wertvolle Möbel, Elektro- oder Sammlerstücke rechnen wir an und senken so Ihren Endpreis." },
  { q: "Übernehmen Sie auch Messie-Wohnungen?", a: "Ja, wir arbeiten sensibel und diskret – ohne Vorwürfe, ohne Aufsehen." },
];

const OBJECT_TYPES = ["Wohnung", "Haus", "Keller", "Dachboden", "Garage", "Gewerbeobjekt"];
const FLOORS = ["EG", "1", "2", "3", "4+"];
const EXTRAS = ["Besenreine Übergabe", "Wertanrechnung", "Entsorgungsnachweis", "Kurzfristiger Termin"];

export default function ClearancePage() {
  const navigate = useNavigate();
  const { cms } = useCms();
  const rawPhone = (cms?.legal?.impressum?.phone) || "+49 176 56198464";
  const digits = rawPhone.replace(/\D/g, "") || "4917656198464";
  const waHref = `https://wa.me/${digits}?text=${encodeURIComponent("Hallo, ich habe eine Anfrage für eine Entrümpelung.")}`;

  const [form, setForm] = useState({
    first_name: "",
    phone: "",
    email: "",
    desired_date: "",
    address: "",
    object_type: "Wohnung",
    size_m2: "",
    floor: "EG",
    elevator: false,
    extras: [],
    message: "",
    photo_ids: [],
    photos: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "Entrümpelung Aachen, Köln & Düsseldorf | Festpreis & besenrein";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Entrümpelung und Haushaltsauflösung in Aachen, Köln und Düsseldorf: kostenlose Besichtigung, Festpreis, Entsorgungsnachweis, besenreine Übergabe.");

    // Inject JSON-LD for Service (removed on unmount)
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-injected", "clearance");
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Entrümpelung, Haushaltsauflösung, Wohnungsauflösung",
      "provider": {
        "@type": "MovingCompany",
        "name": "Leukas Umzug Service",
        "url": "https://leukasumzug.com/",
        "telephone": "+49 176 56198464",
      },
      "areaServed": ["Aachen", "Köln", "Düsseldorf"],
      "description": "Entrümpelung und Haushaltsauflösung in Aachen, Köln und Düsseldorf: kostenlose Besichtigung, Festpreis, Entsorgungsnachweis, besenreine Übergabe.",
    });
    document.head.appendChild(ld);
    track({ custom: "ClearanceViewed", params: { content_name: "Entrümpelung" } });

    return () => {
      const injected = document.querySelector('script[data-injected="clearance"]');
      if (injected) injected.remove();
    };
  }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleExtra = (extra) =>
    setForm((p) => ({ ...p, extras: p.extras.includes(extra) ? p.extras.filter((e) => e !== extra) : [...p.extras, extra] }));

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        uploaded.push({ id: data.id, url: data.url });
      }
      setForm((p) => ({
        ...p,
        photos: [...p.photos, ...uploaded],
        photo_ids: [...p.photo_ids, ...uploaded.map((u) => u.id)],
      }));
    } catch (err) {
      toast.error("Foto-Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) => {
    setForm((p) => ({
      ...p,
      photos: p.photos.filter((_, i) => i !== idx),
      photo_ids: p.photo_ids.filter((_, i) => i !== idx),
    }));
  };

  const validate = () => {
    if (!form.first_name.trim()) return "Bitte Ihren Namen angeben.";
    if (!form.phone.trim()) return "Bitte Ihre Telefonnummer angeben.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return "Bitte eine gültige E-Mail-Adresse angeben.";
    if (!form.desired_date) return "Bitte Wunschtermin angeben.";
    if (!form.address.trim()) return "Bitte Adresse oder PLZ angeben.";
    if (!form.size_m2 || Number(form.size_m2) <= 0) return "Bitte Größe in m² angeben.";
    return null;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    // ---- STEP 1: build wa.me URL from current form values ----
    const digitsClean = digits || FALLBACK_WA;
    const waUrl = whatsappLink(digitsClean, buildClearanceWaMessage({
      first_name: form.first_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      object_type: form.object_type,
      size_m2: form.size_m2,
      floor: form.floor,
      elevator: form.elevator,
      desired_date: form.desired_date,
      extras: form.extras,
      message: form.message.trim(),
    }));

    // ---- STEP 2: SYNCHRONOUS window.open BEFORE any async work ----
    // Must be the first thing after validation so the user gesture chain
    // isn't broken (mobile Safari blocks otherwise).
    const win = window.open(waUrl, "_blank");
    const popupBlocked = !win;

    // ---- STEP 3: fire-and-forget POST — never await, never block ----
    api.post("/clearance-quotes", {
      first_name: form.first_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      object_type: form.object_type,
      size_m2: Number(form.size_m2),
      floor: form.floor,
      elevator: form.elevator,
      extras: form.extras,
      desired_date: form.desired_date,
      message: form.message.trim(),
      photo_ids: form.photo_ids,
    }).catch(() => { /* lead is captured in WhatsApp anyway */ });

    // ---- STEP 4: fire tracking (non-blocking) ----
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18132555642/XmO9CKa8-9IcEPquo8ZD",
          currency: "EUR",
        });
      }
    } catch (_) {}
    try {
      if (typeof window.fbq === "function") {
        window.fbq("trackCustom", "ClearanceFormSubmitted", { content_name: "Entruempelung" });
      }
    } catch (_) {}

    // ---- STEP 5: persist wa URL for fallback + navigate ----
    try {
      sessionStorage.setItem("leukas_last_wa", JSON.stringify({ url: waUrl, popupBlocked }));
    } catch (_) {}
    window.location.href = "/danke-entruempelung";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-slate-200/50 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-amber-100/40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-14 pb-10">
          <div className="grid lg:grid-cols-[1.1fr,1fr] gap-8 lg:gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-soft">
                <Trash2 size={14} className="text-brand-amberDark" /> Entrümpelung · Aachen · Köln · Düsseldorf
              </div>
              <h1 className="mt-4 text-[26px] leading-[1.15] sm:text-4xl lg:text-5xl font-heading font-extrabold tracking-tight text-slate-900" data-testid="clearance-h1">
                Entrümpelung in Aachen, Köln und Düsseldorf – <span className="text-brand-amberDark">schnell, diskret, zum Festpreis</span>
              </h1>
              <p className="mt-3 sm:mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                Wohnung, Haus, Keller oder Nachlass: Wir räumen, entsorgen fachgerecht und übergeben besenrein. Kostenlose Besichtigung.
              </p>

              <div className="mt-5 rounded-2xl bg-white border border-slate-200 shadow-soft px-3 sm:px-5 py-3">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] sm:text-sm text-slate-700">
                  <span className="inline-flex items-center gap-1.5 font-semibold"><CheckCircle2 size={14} className="text-brand-amberDark" /> Festpreis nach Besichtigung</span>
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={14} className="text-slate-500" /> Besenreine Übergabe</span>
                  <span className="inline-flex items-center gap-1.5"><FileText size={14} className="text-slate-500" /> Entsorgungsnachweis</span>
                  <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-slate-500" /> Termin oft in 7 Tagen</span>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-amber text-brand-navy px-6 py-3.5 font-bold hover:bg-amber-300 transition-colors"
                  data-testid="hero-primary-cta"
                >
                  Kostenloses Angebot anfordern
                </button>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-green-500 text-green-700 bg-white px-6 py-3.5 font-semibold hover:bg-green-50 transition-colors"
                  data-testid="hero-wa-cta"
                >
                  <MessageCircle size={16} /> Per WhatsApp fragen
                </a>
              </div>
            </div>

            {/* Lead form */}
            <form
              ref={formRef}
              id="lead-form"
              onSubmit={onSubmit}
              className="rounded-3xl bg-white border border-slate-200 shadow-floating p-5 sm:p-6"
              data-testid="clearance-form"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Kostenloses Angebot</div>
              <h2 className="mt-1 text-xl sm:text-2xl font-heading font-bold text-slate-900">In 2 Minuten anfragen</h2>

              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Name *</label>
                    <input type="text" required value={form.first_name} onChange={(e) => setField("first_name", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-name" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Telefon *</label>
                    <input type="tel" required value={form.phone} onChange={(e) => setField("phone", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-phone" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">E-Mail *</label>
                  <input type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Wunschtermin *</label>
                    <input type="date" required value={form.desired_date} onChange={(e) => setField("desired_date", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-date" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Adresse / PLZ *</label>
                    <input type="text" required value={form.address} onChange={(e) => setField("address", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-address" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Objektart *</label>
                    <select value={form.object_type} onChange={(e) => setField("object_type", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-object">
                      {OBJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Größe ca. (m²) *</label>
                    <input type="number" min="1" required value={form.size_m2} onChange={(e) => setField("size_m2", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-size" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Etage</label>
                    <select value={form.floor} onChange={(e) => setField("floor", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-floor">
                      {FLOORS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 pt-6 text-sm text-slate-700">
                    <input type="checkbox" checked={form.elevator} onChange={(e) => setField("elevator", e.target.checked)} className="w-4 h-4" data-testid="cf-elevator" />
                    Aufzug vorhanden
                  </label>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">Zusatzleistungen</label>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {EXTRAS.map((ex) => (
                      <label key={ex} className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={form.extras.includes(ex)} onChange={() => toggleExtra(ex)} className="w-4 h-4" data-testid={`cf-extra-${ex}`} />
                        {ex}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">Fotos hochladen (optional)</label>
                  <label className="mt-1 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 py-3 cursor-pointer hover:bg-slate-50 transition-colors">
                    <input type="file" accept="image/*" multiple onChange={onPickPhotos} className="hidden" data-testid="cf-photos-input" />
                    {uploading ? <Loader2 className="animate-spin text-slate-600" size={16} /> : <Upload size={16} className="text-slate-600" />}
                    <span className="text-sm text-slate-600">{uploading ? "Wird hochgeladen…" : "Fotos auswählen"}</span>
                  </label>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Fotos können Sie uns direkt im WhatsApp-Chat senden.
                  </p>
                  {form.photos.length > 0 && (
                    <div className="mt-2 grid grid-cols-4 gap-1.5">
                      {form.photos.map((p, i) => {
                        const src = p.url && p.url.startsWith("http") ? p.url : `${BACKEND_URL}${p.url}`;
                        return (
                          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 bg-white/90 hover:bg-white rounded p-0.5">
                              <X size={12} className="text-red-600" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">Nachricht (optional)</label>
                  <textarea rows="3" value={form.message} onChange={(e) => setField("message", e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid="cf-message" />
                </div>
              </div>

              <button type="submit" disabled={submitting}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-amber text-brand-navy px-5 py-3.5 font-extrabold hover:bg-amber-300 disabled:opacity-60 transition-colors"
                data-testid="cf-submit">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <MessageCircle size={16} />}
                Anfrage per WhatsApp senden
              </button>
              <p className="mt-2 text-center text-[11px] text-slate-500">
                Ihre Daten werden automatisch in WhatsApp eingetragen – Sie müssen nur noch auf Senden tippen.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* WAS WIR ENTRÜMPELN */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="clearance-what">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Leistungen</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Was wir entrümpeln</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {WHAT_WE_CLEAR.map((s, i) => (
            <div key={i} className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft hover:shadow-floating transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-brand-amberDark grid place-items-center mb-3">
                <s.icon size={20} />
              </div>
              <h3 className="font-heading font-semibold text-slate-900 text-base">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-snug">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="clearance-process">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">In 4 Schritten</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">So läuft Ihre Entrümpelung ab</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {PROCESS.map((s, i) => (
            <div key={i} className="relative rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft">
              <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                Schritt {i + 1}
              </div>
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-brand-amberDark grid place-items-center mb-3">
                <s.icon size={20} />
              </div>
              <h3 className="font-heading font-semibold text-slate-900 text-base">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-snug">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="clearance-reassurance">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {REASSURANCE.map((r, i) => (
            <div key={i} className="rounded-3xl bg-brand-navy text-white p-5 sm:p-6 shadow-floating">
              <div className="w-11 h-11 rounded-xl bg-amber-400/15 text-brand-amber grid place-items-center mb-3">
                <r.icon size={20} />
              </div>
              <h3 className="font-heading font-semibold text-base">{r.title}</h3>
              <p className="text-sm text-slate-300 mt-1 leading-snug">{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="clearance-region">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Wo wir arbeiten</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Unser Einsatzgebiet</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {REGION_COLS.map((col, i) => (
            <div key={i} className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-amber-50 text-brand-amberDark grid place-items-center"><MapPin size={16} /></span>
                <h3 className="font-heading font-bold text-slate-900 text-base">{col.title}</h3>
              </div>
              <p className="text-[14px] sm:text-[15px] text-slate-600 leading-relaxed">
                {col.places.join(", ")}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-slate-600 max-w-2xl mx-auto">
          Ihr Ort ist nicht dabei? Fragen Sie uns – wir sind in der ganzen Region unterwegs.
        </p>
      </section>

      {/* PRICING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="clearance-prices">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Preise</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Was kostet eine Entrümpelung?</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PRICE_TIERS.map((t, i) => (
            <div key={i} className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-soft hover:shadow-floating transition-shadow">
              <div className="text-[11px] font-bold uppercase tracking-widest text-brand-amberDark">{t.title}</div>
              <div className="mt-3 font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">{t.price}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-600 max-w-3xl mx-auto text-center">
          Richtwerte. Der verbindliche Festpreis folgt nach kostenloser Besichtigung — abhängig von Menge, Etage, Zugang und Wertanrechnung.
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="clearance-faq">
        <div className="text-center mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Häufige Fragen</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-bold text-slate-900">Alles, was Sie wissen müssen</h2>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 shadow-soft p-2 sm:p-3">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b last:border-b-0 border-slate-100">
                <AccordionTrigger className="px-4 py-4 text-left font-heading font-semibold text-slate-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 text-slate-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Closing CTA */}
        <div className="mt-10 rounded-3xl bg-brand-navy text-white p-6 sm:p-10 text-center shadow-floating">
          <h3 className="text-2xl sm:text-3xl font-heading font-bold">Bereit für Ihre Entrümpelung?</h3>
          <p className="mt-2 text-slate-300">Kostenloses Angebot in 2 Minuten anfragen – Antwort innerhalb von 60 Minuten.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-amber text-brand-navy px-6 py-3 font-bold hover:bg-amber-300 transition-colors">
              Kostenloses Angebot anfordern
            </button>
            <Link to="/" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 px-6 py-3 font-semibold transition-colors">
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
